import RA from "@/components/forms/Radio.module.css";
import R from "@/components/forms/Radiogroup.module.css";
import CD from "@/modules/services/ConnectionDetails.module.css";

import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { ContentPane } from "@/components/ContentPane.tsx";
import { SectionHeader } from "@/components/SectionHeader.tsx";
import { simpleToText, type SimpleElements } from "@/components/renderAsText.ts";
import { useStandardSuspenseQuery } from "@/modules/api/useStandardMutation.ts";
import { ipQueryAtom } from "@/modules/network/ipAddress/ipAddressApi.ts";
import { TextSecret } from "@/modules/services/TextSecret.tsx";
import type { ManagedServiceWithAssigned } from "@/modules/services/joinAssignedQuery.ts";
import { getServiceSecretsQuery } from "@/modules/services/servicesQuery.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans } from "@lingui/react/macro";
import type { ManagedDbService } from "@warrenio/api-spec/spec.oats.gen";
import { exhaustiveSwitchCheck } from "@warrenio/utils/unreachable";
import { useState } from "react";
import { Radio, RadioGroup } from "react-aria-components";

export function ConnectionDetailsContent({ item }: { item: ManagedServiceWithAssigned & ManagedDbService }) {
    const { uuid, properties, service } = item;
    const serviceSecrets = useStandardSuspenseQuery(getServiceSecretsQuery, { uuid });
    const [value, setValue] = useState<"parameters" | "string" | "flags">("parameters");
    const [visible, setVisible] = useState<boolean>(false);

    const ips = useSuspenseQueryAtom(ipQueryAtom(properties.location));
    const attachedPublicIp = [...ips.values()].find(
        (ip) => ip?.assigned_to_resource_type === "service" && ip.assigned_to === uuid,
    );

    const { service_ip, sql_user, port } = properties;

    function createDetails({ host }: { host: string | undefined }) {
        const password = serviceSecrets.data.sql_pass;

        function renderValue(): SimpleElements {
            switch (value) {
                case "parameters":
                    return (
                        <>
                            <b>username</b> = {sql_user}
                            <br />
                            <b>password</b> ={" "}
                            <TextSecret visible={visible} setVisible={setVisible} withButton>
                                {password}
                            </TextSecret>
                            <br />
                            <b>host</b> = {host}
                            <br />
                            <b>port</b> = {port}
                        </>
                    );

                case "string":
                    if (service === "mariadb") {
                        return (
                            <>
                                mysql://{sql_user}:
                                <TextSecret visible={visible} setVisible={setVisible}>
                                    {password}
                                </TextSecret>
                                @{host}:{port}
                            </>
                        );
                    } else if (service === "postgresql") {
                        return (
                            <>
                                postgresql://{sql_user}:
                                <TextSecret visible={visible} setVisible={setVisible}>
                                    {password}
                                </TextSecret>
                                @{host}:{port}
                            </>
                        );
                    } else if (service === "self-service-dummy-testing") {
                        return null;
                    }
                    return exhaustiveSwitchCheck(service);
                case "flags":
                    if (service === "mariadb") {
                        return (
                            <>
                                mysql -u {sql_user} -p'
                                <TextSecret visible={visible} setVisible={setVisible}>
                                    {password}
                                </TextSecret>
                                ' -h {host} -P {port}
                            </>
                        );
                    } else if (service === "postgresql") {
                        return (
                            <>
                                PGPASSWORD='
                                <TextSecret visible={visible} setVisible={setVisible}>
                                    {password}
                                </TextSecret>
                                ' psql -h {host} -p {port} -U {sql_user}
                            </>
                        );
                    }
                    return exhaustiveSwitchCheck(service);
            }
        }

        const tpl = renderValue();
        const copyString = simpleToText(tpl);
        return (
            <>
                {tpl}

                <div className="pt-3">
                    <ClipBoardTooltip text={copyString}>
                        <Trans comment="Full form: Copy to clipboard">Copy</Trans>
                    </ClipBoardTooltip>
                </div>
            </>
        );
    }

    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader
                title={
                    <Trans comment="Title for section that contains credentials to connect to a database">
                        Connection details
                    </Trans>
                }
            />

            <div>
                <RadioGroup
                    className={R.RadioGroup}
                    aria-label="Connection details type"
                    value={value}
                    orientation="horizontal"
                    onChange={(v) => setValue(v as typeof value)}
                >
                    <Radio className={RA.Radio} value="parameters">
                        <Trans>Parameters</Trans>
                    </Radio>
                    <Radio className={RA.Radio} value="string">
                        <Trans>String</Trans>
                    </Radio>
                    <Radio className={RA.Radio} value="flags">
                        <Trans>Flags</Trans>
                    </Radio>
                </RadioGroup>
            </div>

            <div className={CD.Details}>
                {attachedPublicIp?.address && (
                    <div className="lightBlock">
                        <h3 className="pb-3">
                            <Trans>Public network</Trans>
                        </h3>

                        {createDetails({ host: attachedPublicIp?.address })}
                    </div>
                )}

                <div className="lightBlock">
                    <h3 className="pb-3">
                        <Trans>VPC network</Trans>
                    </h3>

                    {createDetails({ host: service_ip })}
                </div>
            </div>
        </ContentPane>
    );
}

// cspell:words PGPASSWORD
